/**
 * Discount Code Component
 * Handles discount code application in cart
 */
export default function discountCode() {
  return {
    code: '',
    applying: false,
    removing: false,
    error: '',
    expanded: false,

    get appliedDiscountCode() {
      // Get the discount code from the cart if available
      const discountInput = this.$el?.querySelector('input[readonly]');
      return discountInput?.value || '';
    },

    async removeDiscount() {
      this.removing = true;
      this.error = '';

      try {
        // Remove discount using /cart/update.js endpoint with empty discount string
        // According to Shopify Ajax Cart API: https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-updatejs
        // Use locale-aware URL with .js extension to ensure JSON response (not HTML redirect)
        const rootUrl =
          window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
        const cartUpdateUrl = rootUrl + 'cart/update.js';

        const response = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: '',
            sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
            sections_url: window.location.pathname,
          }),
          redirect: 'follow',
        });

        // Check if the request was successful
        if (response.ok) {
          // Shopify .js endpoints return text/javascript, not application/json
          // Parse the response as JSON
          const data = await response.json();

          // Check if there was an error in the response
          if (data.status) {
            throw new Error(
              data.message || data.description || 'Failed to remove discount'
            );
          }

          // Verify we have the required sections from bundled section rendering
          if (
            data.sections &&
            data.sections['cart-footer'] &&
            data.sections['cart-items']
          ) {
            // Clear any errors first
            this.error = '';

            // Trigger cart update event on document with bubbles to match theme pattern
            document.dispatchEvent(
              new CustomEvent('shapes:cart:update', {
                bubbles: true,
                detail: {
                  response: {
                    sections: data.sections,
                  },
                },
              })
            );
          } else {
            console.error('Missing cart sections in response:', data);
            // Fallback: reload page (for cart page, not drawer)
            window.location.reload();
          }
        } else {
          this.error =
            window.theme?.strings?.cartError ||
            'There was an error while updating your cart. Please try again.';
        }
      } catch (err) {
        console.error('Remove discount error:', err);
        this.error =
          window.theme?.strings?.cartError ||
          'There was an error while updating your cart. Please try again.';
      } finally {
        this.removing = false;
      }
    },

    async applyDiscount() {
      if (!this.code.trim()) {
        this.error =
          window.theme?.strings?.cartDiscountCodeEmpty ||
          'Please enter a discount code.';
        return;
      }

      this.applying = true;
      this.error = '';

      try {
        // Apply discount via Shopify's discount route
        const discountCode = this.code.trim();
        const response = await fetch(
          '/discount/' + encodeURIComponent(discountCode),
          {
            method: 'GET',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
            redirect: 'follow',
          }
        );

        // Wait a bit for the cart to update on the server
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Fetch updated cart to check if discount was actually applied
        const rootUrl =
          window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
        const cartUrl = rootUrl + 'cart.js';
        const cartResponse = await fetch(cartUrl);

        if (!cartResponse.ok) {
          throw new Error('Failed to fetch cart');
        }

        const cartData = await cartResponse.json();

        // Check if discount was actually applied by checking cart_level_discount_applications
        const discountApplied = cartData.cart_level_discount_applications?.some(
          (discount) =>
            discount.title?.toUpperCase() === discountCode.toUpperCase()
        );

        if (discountApplied) {
          // Discount was successfully applied
          // Fetch updated cart sections with all required sections
          const sectionsUrl = `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`;
          const sectionsResponse = await fetch(sectionsUrl);

          if (!sectionsResponse.ok) {
            throw new Error(
              `Failed to fetch cart sections: ${sectionsResponse.status}`
            );
          }

          const sectionsData = await sectionsResponse.json();

          // Verify we have the required sections
          if (
            sectionsData &&
            sectionsData['cart-footer'] &&
            sectionsData['cart-items']
          ) {
            // Clear any errors first
            this.error = '';

            // Trigger cart update event on document with bubbles to match theme pattern
            document.dispatchEvent(
              new CustomEvent('shapes:cart:update', {
                bubbles: true,
                detail: {
                  response: {
                    sections: sectionsData,
                  },
                },
              })
            );

            // Clear the input and collapse
            this.code = '';
            this.expanded = false;
          } else {
            console.error('Missing cart sections in response:', sectionsData);
            // Fallback: reload page (for cart page, not drawer)
            window.location.reload();
          }
        } else {
          // Discount was not applied - determine the reason
          // Check if there are any discounts already applied
          const hasOtherDiscounts =
            cartData.cart_level_discount_applications?.length > 0;

          if (hasOtherDiscounts) {
            // Another discount is already applied
            this.error =
              window.theme?.strings?.cartDiscountCodeAlreadyApplied ||
              'A discount code is already applied to your cart. Please remove it first.';
            // Clear the input
            this.code = '';
          } else {
            // Invalid or inapplicable discount code
            // Replace {{ code }} placeholder if present in translation string
            const invalidMessage =
              window.theme?.strings?.cartDiscountCodeInvalid ||
              `The discount code ${discountCode} is not valid or not applicable to the items in your cart.`;
            // Replace {{ code }} placeholder (handles {{code}}, {{ code }}, etc.)
            this.error = invalidMessage.replace(
              /\{\{\s*code\s*\}\}/g,
              discountCode
            );
            // Clear the input
            this.code = '';
          }
        }
      } catch (err) {
        console.error('Discount code error:', err);
        this.error =
          window.theme?.strings?.cartDiscountCodeError ||
          'There was an error applying the discount code. Please try again.';
        // Clear the input on error
        this.code = '';
      } finally {
        this.applying = false;
      }
    },
  };
}
